/**
 * RetriggerPanel — Form for manually re-triggering the AI pipeline for a specific page.
 *
 * - Text input for page ID with client-side validation
 * - Submit button that disables during in-flight request
 * - Success/error result display
 * - 30-second safety timeout to re-enable the submit button
 *
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 10.1
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { RetriggerResponse } from '../../types/ai-pipeline';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export interface RetriggerPanelProps {
  onRetrigger: (pageId: string) => Promise<RetriggerResponse>;
}

function validatePageId(value: string): string | null {
  if (value.trim().length === 0) {
    return 'Page ID is required.';
  }
  if (value.length > 128) {
    return 'Page ID must be 128 characters or fewer.';
  }
  return null;
}

export function RetriggerPanel({ onRetrigger }: RetriggerPanelProps): React.ReactElement {
  const [pageId, setPageId] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    setPageId(e.target.value);
    setValidationError(null);
    setSuccessMessage(null);
    setErrorMessage(null);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
      e.preventDefault();

      const error = validatePageId(pageId);
      if (error !== null) {
        setValidationError(error);
        return;
      }

      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      // Safety timeout: re-enable after 30 seconds regardless of response
      timeoutRef.current = setTimeout(() => {
        setIsSubmitting(false);
      }, 30_000);

      try {
        const response = await onRetrigger(pageId.trim());
        setSuccessMessage(`Pipeline execution started: ${response.executionArn}`);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
        setErrorMessage(message);
      } finally {
        setIsSubmitting(false);
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    },
    [pageId, onRetrigger],
  );

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="retrigger-page-id">Page ID</Label>
          <Input
            id="retrigger-page-id"
            type="text"
            placeholder="Enter page ID to reprocess"
            value={pageId}
            onChange={handleInputChange}
            error={!!validationError}
            aria-describedby={validationError ? 'retrigger-page-id-error' : undefined}
            className="max-w-md"
          />
          {validationError && (
            <p className="text-sm text-destructive" id="retrigger-page-id-error" role="alert">
              {validationError}
            </p>
          )}
        </div>

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span
                className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent"
                aria-hidden="true"
              />
              Starting...
            </>
          ) : (
            'Re-trigger Pipeline'
          )}
        </Button>
      </form>

      {successMessage && (
        <Alert variant="success" aria-live="polite">
          <AlertDescription>{successMessage}</AlertDescription>
        </Alert>
      )}

      {errorMessage && (
        <Alert variant="destructive" aria-live="assertive">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
